import React, { useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import AuditHistoryTab from '../audit/AuditHistoryTab';
import { UserAvatar, hueOf } from '../users/UserAvatar';
import { clientInitials } from '../clients/clientInitials';
import { deviceTransferService } from '../../services/deviceTransferService';
import { ClientRef, DeviceTransfer, PersonRef } from '../../types/deviceTransfer';
import { TransferStatusBadge } from './TransferStatusBadge';

interface TransferDetailModalProps {
  transfer: DeviceTransfer | null;
  isOpen: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

type Tab = 'details' | 'history';

const personName = (p: PersonRef): string => [p.firstName, p.lastName].filter(Boolean).join(' ') || p.username;
/** A client's name; `hidden` when the environment keeps the current owner from this caller. */
const clientName = (c: ClientRef, hidden: string): string => c.name ?? (c.id || hidden);

/** One party of the take-over: the client's mark, its name, its role. */
const Party: React.FC<{ client: ClientRef; name: string; role: string; testId: string }> = ({ client, name, role, testId }) => (
  <div className="pt-party" data-testid={testId}>
    <span className="pt-mark" style={{ ['--pt-hue' as any]: hueOf(client.id) }} aria-hidden="true">{clientInitials(name)}</span>
    <div className="pt-party-text">
      <span className="pt-party-role">{role}</span>
      <strong className="pt-party-name">{name}</strong>
    </div>
  </div>
);

/** A person: avatar, display name, email beneath. */
const Person: React.FC<{ person: PersonRef }> = ({ person }) => (
  <span className="pt-person">
    <UserAvatar username={person.username} firstName={person.firstName ?? undefined} lastName={person.lastName ?? undefined} />
    <span className="pt-person-text">
      <span className="pt-person-name">{personName(person)}</span>
      {person.email && <span className="pt-person-email">{person.email}</span>}
    </span>
  </span>
);

/**
 * One take-over request: the two clients and what changes, the exchange
 * between the requester and the owner's administrators, the gateways. Any
 * administrator of the owner accepts or refuses here; the requester can
 * withdraw a pending request. The administrators are counted, never named.
 */
export const TransferDetailModal: React.FC<TransferDetailModalProps> = ({ transfer, isOpen, onClose, onChanged }) => {
  const { formatDateTime, t } = useI18n();
  const { isAnepStaff } = useClientAccess();
  const [tab, setTab] = useState<Tab>('details');
  const [comment, setComment] = useState('');
  const [working, setWorking] = useState<'accept' | 'refuse' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTab('details');
    setComment('');
    setError(null);
  }, [transfer, isOpen]);

  if (!isOpen || !transfer) return null;
  const hidden = t('portability.hiddenOwner');

  const act = async (kind: 'accept' | 'refuse' | 'cancel') => {
    const confirmText = kind === 'accept'
      ? t('portability.detail.acceptConfirm', { count: transfer.deviceCount, client: clientName(transfer.toClient, hidden), template: transfer.templateName ?? String(transfer.templateId) })
      : kind === 'refuse' ? t('portability.detail.refuseConfirm') : t('portability.detail.cancelConfirm');
    if (!window.confirm(confirmText)) return;
    setWorking(kind);
    setError(null);
    try {
      if (kind === 'accept') await deviceTransferService.accept(transfer.id, comment.trim() || undefined);
      else if (kind === 'refuse') await deviceTransferService.refuse(transfer.id, comment.trim() || undefined);
      else await deviceTransferService.cancel(transfer.id);
      onChanged?.();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('portability.detail.failed'));
    } finally {
      setWorking(null);
    }
  };

  const decided = transfer.status !== 'pending';
  const moved = transfer.result?.filter((r) => r.moved).length ?? 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="config-template-modal tpd ptd" data-testid="modal-transfer-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <div className="tpd-title-text">
              <h2>{t('portability.detail.title')} <span className="pt-ref">#{transfer.id}</span></h2>
              <div className="tpd-title-sub">
                <span>{t('portability.deviceCount', { count: transfer.deviceCount, type: transfer.deviceType })}</span>
                <span>{clientName(transfer.fromClient, hidden)} → {clientName(transfer.toClient, hidden)}</span>
                <TransferStatusBadge status={transfer.status} />
              </div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        <div className="modal-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'details'} className={`tab-button ${tab === 'details' ? 'active' : ''}`} data-testid="tab-transfer-details" onClick={() => setTab('details')}>{t('portability.detail.tabs.details')}</button>
          <button type="button" role="tab" aria-selected={tab === 'history'} className={`tab-button ${tab === 'history' ? 'active' : ''}`} data-testid="tab-transfer-history" onClick={() => setTab('history')}>{t('portability.detail.tabs.history')}</button>
        </div>

        <div className="modal-content tpd-body">
          {tab === 'history' ? (
            <AuditHistoryTab resourceType="device_transfer" resourceId={String(transfer.id)} />
          ) : (
            <>
              {error && <div className="error-message" role="alert">{error}</div>}

              {/* 1. What the take-over is */}
              <section className="cu-section">
                <header className="cu-section-head">
                  <h3>{t('portability.detail.overview')}</h3>
                  <p>{t('portability.detail.overviewIntro')}</p>
                </header>
                <div className="pt-flow">
                  <Party client={transfer.fromClient} name={clientName(transfer.fromClient, hidden)} role={t('portability.detail.from')} testId="transfer-from" />
                  <span className="pt-arrow" aria-hidden="true">→</span>
                  <Party client={transfer.toClient} name={clientName(transfer.toClient, hidden)} role={t('portability.detail.to')} testId="transfer-to" />
                </div>
                <dl className="tpd-facts">
                  <div className="tpd-fact">
                    <dt>{t('portability.detail.gateways')}</dt>
                    <dd>{t('portability.deviceCount', { count: transfer.deviceCount, type: transfer.deviceType })}</dd>
                  </div>
                  <div className="tpd-fact">
                    <dt>{t('portability.detail.template')}</dt>
                    <dd>{transfer.templateName ?? <span className="tpd-muted">#{transfer.templateId}</span>}</dd>
                  </div>
                  <div className="tpd-fact">
                    <dt>{t('portability.detail.recipients')}</dt>
                    <dd data-testid="transfer-recipients">{t('portability.detail.recipientsValue', { count: transfer.notifiedCount, client: clientName(transfer.fromClient, hidden) })}</dd>
                  </div>
                  <div className="tpd-fact">
                    <dt>{t('portability.detail.status')}</dt>
                    <dd className="pt-status-fact">
                      <TransferStatusBadge status={transfer.status} />
                      {transfer.decidedAt && <span className="tpd-muted">{formatDateTime(transfer.decidedAt)}</span>}
                    </dd>
                  </div>
                </dl>
              </section>

              {/* 2. The exchange: the request, then the answer */}
              <section className="cu-section">
                <header className="cu-section-head">
                  <h3>{t('portability.detail.exchange')}</h3>
                  <p>{t('portability.detail.exchangeIntro')}</p>
                </header>
                <div className="pt-thread">
                  <article className="pt-bubble" data-testid="transfer-message">
                    <header className="pt-bubble-head">
                      <Person person={transfer.requestedBy} />
                      <span className="pt-bubble-meta">{transfer.createdAt ? t('portability.detail.requestedOn', { date: formatDateTime(transfer.createdAt) }) : t('portability.detail.requestedBy')}</span>
                    </header>
                    <p className="pt-bubble-body">{transfer.message || <span className="tpd-muted">{t('portability.detail.noComment')}</span>}</p>
                  </article>

                  {decided && (
                    <article className="pt-bubble is-answer" data-status={transfer.status} data-testid="transfer-answer">
                      <header className="pt-bubble-head">
                        <span className="pt-person">
                          <UserAvatar username={transfer.decidedBy ?? '?'} />
                          <span className="pt-person-text">
                            <span className="pt-person-name">{transfer.decidedBy ?? '–'}</span>
                            <span className="pt-person-email">{t(`portability.detail.answerKind.${transfer.status}` as any)}</span>
                          </span>
                        </span>
                        <span className="pt-bubble-meta">
                          <TransferStatusBadge status={transfer.status} />
                          {transfer.decidedAt && <span>{t('portability.detail.answeredOn', { date: formatDateTime(transfer.decidedAt) })}</span>}
                        </span>
                      </header>
                      <p className="pt-bubble-body">{transfer.decisionComment || <span className="tpd-muted">{t('portability.detail.noComment')}</span>}</p>
                    </article>
                  )}

                  {!decided && transfer.canDecide && (
                    <article className="pt-bubble is-mine" data-testid="transfer-your-answer">
                      <header className="pt-bubble-head">
                        <strong>{t('portability.detail.yourAnswer')}</strong>
                      </header>
                      {isAnepStaff && <p className="cu-note">{t('portability.detail.anepOverride', { client: clientName(transfer.fromClient, hidden) })}</p>}
                      <div className="cu-field cu-field-wide">
                        <label htmlFor="transfer-comment">{t('portability.detail.comment')}</label>
                        <textarea id="transfer-comment" data-testid="field-transfer-comment" className="cu-input" rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} disabled={working !== null} placeholder={t('portability.detail.commentPlaceholder')} />
                      </div>
                    </article>
                  )}

                  {!decided && !transfer.canDecide && (
                    <p className="cu-note pt-waiting">{t('portability.detail.waiting', { client: clientName(transfer.fromClient, hidden) })}</p>
                  )}
                </div>
              </section>

              {/* 3. The gateways, with the outcome once moved */}
              <section className="cu-section">
                <header className="cu-section-head">
                  <h3>{t('portability.detail.devices', { count: transfer.deviceCount })}</h3>
                  <p>{transfer.status === 'accepted' ? t('portability.detail.devicesMoved', { moved, count: transfer.deviceCount }) : t('portability.detail.devicesIntro')}</p>
                </header>
                <ul className="pt-devices" data-testid="transfer-devices">
                  {transfer.deviceIds.map((imei) => {
                    const outcome = transfer.result?.find((r) => r.idDevice === imei);
                    return (
                      <li key={imei} className={outcome && !outcome.moved ? 'is-problem' : ''}>
                        <code className="dt-code">{imei}</code>
                        <span className="pt-device-type">{transfer.deviceType}</span>
                        {outcome && (
                          <span className={`pt-device-state ${outcome.moved ? 'is-moved' : 'is-failed'}`} title={outcome.detail ?? undefined}>
                            {outcome.moved ? t('portability.detail.resultMoved') : t('portability.detail.resultFailed')}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </>
          )}
        </div>

        <div className="modal-actions tpd-footer">
          <button type="button" className="btn btn-secondary" data-testid="btn-transfer-close" onClick={onClose}>{t('common.close')}</button>
          {tab === 'details' && transfer.canCancel && (
            <button type="button" className="btn btn-danger-outline" data-testid="btn-transfer-cancel" onClick={() => act('cancel')} disabled={working !== null}>
              {working === 'cancel' ? t('portability.detail.working') : t('portability.detail.cancel')}
            </button>
          )}
          {tab === 'details' && transfer.canDecide && (
            <>
              <button type="button" className="btn btn-danger-outline" data-testid="btn-transfer-refuse" onClick={() => act('refuse')} disabled={working !== null}>
                {working === 'refuse' ? t('portability.detail.working') : t('portability.detail.refuse')}
              </button>
              <button type="button" className="btn btn-primary" data-testid="btn-transfer-accept" onClick={() => act('accept')} disabled={working !== null}>
                {working === 'accept' ? t('portability.detail.working') : t('portability.detail.accept')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
